import {HttpClient, HttpHeaders, HttpResponse} from '@angular/common/http';
import {inject} from '@angular/core';
import {FileSaverService} from 'ngx-filesaver';
import {throwError} from 'rxjs';
import {RequestOption} from '../model/request-options';
import {ConfigService} from './config.service';
import {LogService} from './log.service';
import { HttpErrorResponse } from '@angular/common/http';

export class CommonService {
  _FileSaverService: FileSaverService = inject(FileSaverService);
  constructor(
    protected http: HttpClient,
    protected configService: ConfigService,
    protected logger: LogService,
  ) {}

  protected baseUrl: string = this.configService.getServer();
  protected langId: string = localStorage.getItem('langId');

  // used for token revoke requests
  protected extractStatus(res: HttpResponse<any>) {
    let status = res.status;
    return status;
  }

  protected extractData(res: HttpResponse<any>): any {
    let body: any = res;
    if (!!body && body['status'] == 'S') {
      body = body['data'];
      try {
        if (!!body && typeof body === 'string') {
          body = JSON.parse(body);
        }
      } catch (e) {}
    }

    if (!!body && body['status'] == 'S_N') {
      LogService.emitAlert({
        type: body['message'] == 'OK' ? 'success' : 'info',
        body: body['message'],
      });
    }

    if (!!body && body['status'] == 'W') {
      body = Object.assign(body, {submitError: true});
    }

    body = body == null ? {} : body;
    return body;
  }

  protected saveData(res: HttpResponse<any>): any {
    let body = res.body.json();
    this.configService.getIsSessionStorage()
      ? sessionStorage.setItem('access_token', body.access_token.toString())
      : localStorage.setItem('access_token', body.access_token.toString());
    return body;
  }

  protected getActiveLang() {
    return localStorage.getItem('langId');
  }

  public saveToStorageWithName(res: HttpResponse<any>, name: string) {
    localStorage.setItem(name, JSON.stringify(res));
  }

  public getLkpFromStorageAsJSON(name: string, required?: boolean): any {
    let body = JSON.parse(localStorage.getItem(name + '_' + this.getActiveLang()));
    if (!required) {
      if (Array.isArray(body)) {
        body.unshift({id: ' ', text: ' '});
      }
    }
    return body;
  }

  public getFromStorageAsJSON(name: string, lang = true) {
    return JSON.parse(localStorage.getItem(name + (lang ? '_' + this.getActiveLang() : '')));
  }

  public saveToStorage(key: string, value: string) {
    this.configService.getIsSessionStorage()
      ? sessionStorage.setItem(key, value)
      : localStorage.setItem(key, value);
  }

  public getFromStorage(key: string): string {
    return this.configService.getIsSessionStorage()
      ? sessionStorage.getItem(key)
      : localStorage.getItem(key);
  }

  public clearStorages() {
    localStorage.clear();
    sessionStorage.clear();
    localStorage.setItem(
      'langId',
      this.configService.getLangDefault() ? this.configService.getLangDefault().toString() : '2',
    );
  }

  protected setPostParams(body: any, extraOptions: {} = {}): RequestOption {
    let options = {
      method: 'POST',
      body: body,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }).append('Authorization', 'Bearer ' + this.getFromStorage('access_token')),
      ...extraOptions,
    };
    return options;
  }

  protected setGetParams(body: any): RequestOption {
    let options = {
      method: 'GET',
      params: body,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return options;
  }

  public extractTxt(res: HttpResponse<string>) {
    let blob = new Blob([res.body], {type: 'text/plain;charset=utf-8'});

    let filename = (
      (decodeURI(res.headers.get('X-Attachment-Name')) || 'results') + '.txt'
    ).replace(/\+/g, ' ');
    this._FileSaverService.save(blob, filename);
  }

  public extractCsv(res: HttpResponse<any>) {
    let filename = (
      (decodeURI(res.headers.get('X-Attachment-Name')) || 'results') + '.csv'
    ).replace(/\+/g, ' ');

    let link = window.document.createElement('a');
    link.setAttribute('href', 'data:text/csv;charset=utf-8,%EF%BB%BF' + encodeURI(res.body));
    link.setAttribute('download', filename);
    link.click();
  }

  public extractXls(res: HttpResponse<any>) {
    let blob = new Blob([res.body], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    let filename = (
      (decodeURI(res.headers.get('X-Attachment-Name')) || 'results') + '.xlsx'
    ).replace(/\+/g, ' ');
    this._FileSaverService.save(blob, filename);
  }

  public extractPdf(res: HttpResponse<Blob>) {
    let blob = new Blob([res.body], {type: 'application/pdf'});
    let filename = (
      (decodeURI(res.headers.get('X-Attachment-Name')) || 'results') + '.pdf'
    ).replace(/\+/g, ' ');
    this._FileSaverService.save(blob, filename);
  }

  /**
   * Only allow console.* (not this.logger) here
   */
  public handleError(error: HttpResponse<any> | any) {
    let errMsg: string;
    //uncomment to display full stacktrace
    console.error(error);

    if (error instanceof HttpErrorResponse) {
      /*
       *  Handle all cases of (error)_body type
       * ( HTML , JSON, String )
       */
      let errorTxt = error.error.message;
      //HTML Regex test
      if (/<(?=.*? .*?\/ ?>|br|hr|input|!--|wbr)[a-z]+.*?>|<([a-z]+).*?<\/\1>/i.test(errorTxt)) {
        errMsg = `(${error.status}) ${error.statusText || ''}.`;
       
      } else {
        //try JSON parse
        try {
          let errorJSON: JSON = error.error;
          if (Object.keys(errorJSON).length !== 0) {
            if (errorJSON.hasOwnProperty('error_description')) {
              errMsg = errorJSON['error_description'];
            } else {
              throw new Error('Empty');
            }
          } else {
            errorTxt = '';
            throw new Error('Empty');
          }
        } catch (e) {
          //not JSON so it is assumed string
          errMsg = errorTxt || `(${error.status}) ${error.statusText || ''}.` + errorTxt;
        }
      }
    } else {
      errMsg = error.message ? error.message : error.toString();
    }

    if (error.status != 0 && error.status != 401) {
      try {
        var errorMessage = JSON.parse(errMsg);
        LogService.emitAlert({
          type: 'error',
          body: errorMessage,
        });
      } catch (e) {
        errorMessage = errMsg;
        LogService.emitAlert({
          type: 'error',
          body: errorMessage,
        });
      }
    }

    //XHTTP down - unavailable error
    //but don't emmit only return it
    if (error.status == 0) {
      errMsg = 'Service Unavailable';
    }

    console.error('Service Error: ' + errMsg);
    return throwError(() => new Error(errMsg));
  }
}
